import {
    pickFolderAssignment,
    splitTaskAssignmentTargetId,
    taskAssignmentTargetId,
} from "routes/skill-mapper/server/schedule-assignment-routing.mjs"

export const NO_FOLDER_KEY = "__no-folder__"

/**
 * Turn the regime summary, the skill map's assignments and its skill lines into what the
 * assignment board draws (FIQ-2457). Routes through the same helpers as the cost engine:
 * a schedule assignment beats any folder, folders resolve across every place the schedule
 * is filed in the library (pickFolderAssignment), and task assignments only move that
 * task's hours.
 *
 * @param {{
 *   schedules: object[],   // scheduleAssignmentRegime(...).schedules
 *   assignments: object[], // skillMapScheduleAssignments
 *   lines: object[],       // skillMapLines
 *   trades: Object<string, string>,
 *   search?: string,
 * }} input
 * @returns {{ groups: object[], allGroups: object[], trades: object[], progress: object, schedulesById: Map, scheduleTrade: Map, scheduleAssignments: Map, scheduleFolderAssignments: Map, taskAssignments: Map, folderAssignments: Map, folderRouted: object[], linesBySkill: Map }}
 *   scheduleTrade maps a schedule id to the trade its schedule or folder assignment routes it to;
 *   scheduleAssignments to that assignment itself, so the drawer can say where a trade comes from.
 *   allGroups are the groups before the search filters them, so unticking a row while
 *   searching can keep the hidden schedules a folder tick covered (see board-selection).
 *   folderRouted lists the schedules a folder assignment routes, with every placement, so
 *   assigning another folder can tell which of them it would take over (see folderTakeovers)
 */
export function buildAssignmentBoard({ schedules = [], assignments = [], lines = [], trades = {}, search = "" }) {
    const liveAssignments = assignments.filter(hasLiveTrade)
    const byKind = indexByKind(liveAssignments)
    const linesBySkill = new Map(lines.map(toSkillEntry))
    const needle = search.trim().toLowerCase()
    const tradeTotals = new Map(Object.keys(trades).map(toEmptyTotal))
    const groups = new Map()
    const allGroups = new Map()
    const schedulesById = new Map()
    const scheduleTrade = new Map()
    const scheduleAssignments = new Map()
    // The folder assignment each schedule would follow without an assignment of its own, so
    // the drawer can name what takes over when its whole-schedule trade is set back
    const scheduleFolderAssignments = new Map()
    const folderRouted = []
    let assignedSchedules = 0
    // Tasks given their own trade inside schedules that otherwise use skill routing
    let assignedTasks = 0
    let assignedHours = 0
    let totalHours = 0

    for (const schedule of schedules) {
        schedulesById.set(schedule.scheduleId, schedule)
        const assignment = effectiveAssignment(schedule)
        scheduleTrade.set(schedule.scheduleId, assignment?.tradeId ?? null)
        scheduleAssignments.set(schedule.scheduleId, assignment)
        scheduleFolderAssignments.set(schedule.scheduleId, folderAfterRemoving(schedule, null) ?? null)
        totalHours += schedule.annualHours
        if (assignment) {
            assignedSchedules++
            tradeTotals.get(assignment.tradeId).scheduleCount++
            if (assignment.kind === "folder") {
                folderRouted.push({ scheduleId: schedule.scheduleId, placements: placementsOf(schedule), assignment })
            }
        } else {
            const listed = toListedSchedule(schedule)
            addToGroup(allGroups, schedule, listed)
            if (matchesSearch(schedule)) addToGroup(groups, schedule, listed)
        }
        for (const task of schedule.tasks || []) {
            const taskAssignment = byKind.task.get(taskAssignmentTargetId(schedule.scheduleId, task.taskId))
            const tradeId = taskAssignment?.tradeId ?? assignment?.tradeId
            if (!tradeId) continue
            if (taskAssignment && !assignment) assignedTasks++
            tradeTotals.get(tradeId).annualHours += task.annualHours
            assignedHours += task.annualHours
        }
    }

    const listedGroups = [...groups.values()].sort(byLabel)
    return {
        groups: listedGroups,
        // Without a search nothing is hidden, so both are the same list
        allGroups: needle ? [...allGroups.values()].sort(byLabel) : listedGroups,
        trades: Object.entries(trades).map(toTradeBucket).sort(byTradeName),
        progress: { assignedSchedules, assignedTasks, totalSchedules: schedules.length, assignedHours, totalHours },
        schedulesById,
        scheduleTrade,
        scheduleAssignments,
        scheduleFolderAssignments,
        taskAssignments: byKind.task,
        folderAssignments: byKind.folder,
        folderRouted,
        linesBySkill,
    }

    // Own keys only, so a tradeId such as "constructor" never matches Object.prototype
    function hasLiveTrade(assignment) {
        return Object.hasOwn(trades, assignment.tradeId)
    }

    function effectiveAssignment(schedule) {
        const direct = byKind.schedule.get(schedule.scheduleId)
        if (direct) return direct
        return pickFolderAssignment(placementsOf(schedule), folderAssignmentFor)
    }

    function folderAssignmentFor(folderId) {
        return byKind.folder.get(folderId)
    }

    function matchesSearch(schedule) {
        if (!needle) return true
        return `${schedule.code} ${schedule.title}`.toLowerCase().includes(needle)
    }

    function addToGroup(into, schedule, listed) {
        const path = schedule.folderPath || []
        const folder = path.at(-1)
        const key = folder?.id ?? NO_FOLDER_KEY
        if (!into.has(key)) {
            into.set(key, {
                key,
                folderId: folder?.id ?? null,
                label: path.length ? path.map(toName).join(" › ") : "Not in the schedule library",
                schedules: [],
            })
        }
        into.get(key).schedules.push(listed)
    }

    function toListedSchedule(schedule) {
        return {
            ...schedule,
            // Every folder a folder assignment could route this schedule through, in any
            // placement, so ticking one of those folders shows this schedule as covered
            folderIds: [...new Set(placementsOf(schedule).flat())],
            routing: describeSkillRouting(schedule),
            // The routing label shows each skill's default trade; these rules can send some
            // of the schedule's tasks elsewhere, so the row flags them instead of hiding them
            conditionalRules: conditionalRulesFor((schedule.skills || []).map(toSkillId)),
        }
    }

    function describeSkillRouting(schedule) {
        const names = new Set()
        for (const { skillId } of schedule.skills || []) {
            const tradeId = linesBySkill.get(skillId)?.tradeId
            names.add(skillId === "" ? "No skill" : trades[tradeId] || "Unmapped")
        }
        const list = [...names].sort()
        return list.length > 1 ? `Mixed: ${list.join(", ")}` : list[0] || ""
    }

    function toTradeBucket([tradeId, tradeName]) {
        const chips = liveAssignments.filter(isForTrade).map(toChip)
        const skillCount = [...linesBySkill.values()].filter(isForTrade).length
        return { tradeId, tradeName, chips, skillCount, ...tradeTotals.get(tradeId) }

        // Assignments and skill lines both carry the trade they route to
        function isForTrade(record) {
            return record.tradeId === tradeId
        }
    }

    function toChip(assignment) {
        return {
            ...assignment,
            label: chipLabel(assignment),
            // The schedule a task chip's task belongs to, named in its tooltip rather than its label
            schedule: assignment.kind === "task" ? taskScheduleName(assignment) : null,
            // Whether its work is in the regime being viewed: a schedule or task chip from
            // another regime cannot open its tasks here, and says so instead of doing nothing
            inRegime: isInRegime(assignment),
            overriddenRules: overriddenRulesFor(assignment),
            removal: describeRemoval(assignment),
            // A folder's schedules leave the left-hand list, so its chip lists them instead,
            // keeping each one's task drawer reachable for task-level overrides
            schedules: assignment.kind === "folder" ? schedulesRoutedBy(assignment).map(toScheduleOption) : [],
        }
    }

    // A task chip leads with its schedule code: titles such as "Monthly" repeat across schedules
    function chipLabel(assignment) {
        if (assignment.kind === "task") return taskChipLabel(assignment)
        if (assignment.name) return assignment.name
        return schedulesById.get(assignment.targetId)?.code || assignment.targetId
    }

    function taskChipLabel(assignment) {
        const schedule = schedulesById.get(splitTaskAssignmentTargetId(assignment.targetId).scheduleId)
        const title = taskOf(assignment)?.title
        if (schedule?.code && title) return `${schedule.code} · ${title}`
        return assignment.name || "Task"
    }

    function isInRegime(assignment) {
        if (assignment.kind === "folder") return true
        const scheduleId =
            assignment.kind === "task"
                ? splitTaskAssignmentTargetId(assignment.targetId).scheduleId
                : assignment.targetId
        return schedulesById.has(scheduleId)
    }

    function taskScheduleName(assignment) {
        const schedule = schedulesById.get(splitTaskAssignmentTargetId(assignment.targetId).scheduleId)
        if (schedule) return `${schedule.code} ${schedule.title}`.trim()
        return taskNamePart(assignment.name, 0)
    }

    function taskOf(assignment) {
        const { scheduleId, taskId } = splitTaskAssignmentTargetId(assignment.targetId)
        return schedulesById.get(scheduleId)?.tasks?.find(isTheTask)

        function isTheTask(candidate) {
            return candidate.taskId === taskId
        }
    }

    // What takes over once this assignment is removed, named on the chip's remove button
    function describeRemoval(assignment) {
        if (assignment.kind === "task") return describeTaskRemoval(assignment)
        if (assignment.kind === "schedule") return describeScheduleRemoval(assignment)
        return describeFolderRemoval(assignment)
    }

    function describeTaskRemoval(assignment) {
        const { scheduleId } = splitTaskAssignmentTargetId(assignment.targetId)
        const schedule = schedulesById.get(scheduleId)
        const next = schedule ? effectiveAssignment(schedule) : null
        const text = schedule
            ? `This task will ${describeFollowing(next, "schedule's")}.`
            : "This task will follow its schedule's routing."
        return { title: "Remove task override", text }
    }

    function describeScheduleRemoval(assignment) {
        const schedule = schedulesById.get(assignment.targetId)
        const text = schedule
            ? `This schedule will ${describeFollowing(folderAfterRemoving(schedule, null), "schedule's")}.`
            : "This schedule will follow any folder assignment that covers it, or skill routing."
        return { title: "Remove schedule assignment", text }
    }

    function describeFolderRemoval(assignment) {
        // Removing a folder assignment changes every regime using the map, but the board only
        // loads the one being viewed. So it counts this regime's schedules and, without the
        // other regimes' schedules to say which applies, names both fallbacks for the rest
        const otherRegimes =
            "In other regimes, its schedules will follow another folder assignment that covers them, or go back to skill routing."
        const routed = schedulesRoutedBy(assignment)
        if (!routed.length) {
            return { title: "Remove folder assignment", text: `No schedules in this regime use it. ${otherRegimes}` }
        }
        const toOtherFolder = routed.filter(hasOtherFolder).length
        const toSkill = routed.length - toOtherFolder
        const parts = []
        if (toSkill) parts.push(`${toSkill} ${"schedule".pluralize(toSkill)} will go back to skill routing`)
        if (toOtherFolder) {
            parts.push(
                `${toOtherFolder} ${"schedule".pluralize(toOtherFolder)} will follow another folder's assignment`
            )
        }
        return { title: "Remove folder assignment", text: `In this regime, ${parts.join(", and ")}. ${otherRegimes}` }

        function hasOtherFolder(schedule) {
            return !!folderAfterRemoving(schedule, assignment)
        }
    }

    // The folder assignment a schedule would follow without its own assignment, or without
    // the given folder assignment
    function folderAfterRemoving(schedule, removed) {
        return pickFolderAssignment(placementsOf(schedule), folderAssignmentWithout)

        function folderAssignmentWithout(folderId) {
            const found = folderAssignmentFor(folderId)
            return found === removed ? undefined : found
        }
    }

    function describeFollowing(next, owner) {
        if (!next) return "go back to skill routing"
        const trade = trades[next.tradeId]
        if (next.kind === "folder") return `follow the ${shortFolderName(next.name)} folder assignment (${trade})`
        return `follow the ${owner} assignment (${trade})`
    }

    // Names of conditional skill rules this assignment takes precedence over. A task
    // assignment only overrides the rules on that task's own skill.
    function overriddenRulesFor(assignment) {
        return conditionalRulesFor(skillsCoveredBy(assignment))
    }

    function conditionalRulesFor(skillIds) {
        const names = new Set()
        for (const skillId of skillIds) {
            for (const rule of linesBySkill.get(skillId)?.conditionalTrades || []) names.add(rule.name)
        }
        return [...names].sort()
    }

    function skillsCoveredBy(assignment) {
        if (assignment.kind === "task") return taskSkills(assignment)
        return schedulesRoutedBy(assignment).flatMap((schedule) => (schedule.skills || []).map(toSkillId))
    }

    function taskSkills(assignment) {
        const task = taskOf(assignment)
        return task?.skillId ? [task.skillId] : []
    }

    function schedulesRoutedBy(assignment) {
        if (assignment.kind === "schedule") return [schedulesById.get(assignment.targetId)].filter(Boolean)
        if (assignment.kind === "folder") return schedules.filter(isRoutedBy)
        return []

        function isRoutedBy(schedule) {
            return effectiveAssignment(schedule) === assignment
        }
    }
}

// Task assignments are named "<schedule code> · <task title>" when made; one part of that,
// for a task whose schedule is not in the regime being viewed
function taskNamePart(name, index) {
    const parts = String(name || "").split(" · ")
    return parts.length > 1 ? parts.at(index) : parts[0] || "Task"
}

// Folder assignments carry the full library path ("Mechanical › Boilers"); the last part reads best
export function shortFolderName(name) {
    return String(name || "")
        .split(" › ")
        .at(-1)
}

// Every place the schedule is filed; without placements, only the path it is listed under
function placementsOf(schedule) {
    if (schedule.folderPlacements) return schedule.folderPlacements
    return [(schedule.folderPath || []).map(toFolderId)]
}

function toFolderId(folder) {
    return folder.id
}

function indexByKind(assignments) {
    const byKind = { folder: new Map(), schedule: new Map(), task: new Map() }
    for (const assignment of assignments) byKind[assignment.kind]?.set(assignment.targetId, assignment)
    return byKind
}

function toSkillId(skill) {
    return skill.skillId
}

function toScheduleOption(schedule) {
    return { scheduleId: schedule.scheduleId, code: schedule.code, title: schedule.title }
}

function toSkillEntry(line) {
    return [String(line.skillId), line]
}

function toEmptyTotal(tradeId) {
    return [tradeId, { scheduleCount: 0, annualHours: 0 }]
}

function toName(folder) {
    return folder.name
}

function byLabel(a, b) {
    return a.label.localeCompare(b.label)
}

function byTradeName(a, b) {
    return a.tradeName.localeCompare(b.tradeName)
}
